import { SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IRequestPayload } from 'core/types';
import { manufacturerService } from 'parts/services';
import { IManufacturer } from 'parts/types';

interface IProps {
  name?: string;
  label?: string;
  optionKey?: string;
  requestPayload?: IRequestPayload;
  onChange?: (manufacturer: IManufacturer) => void;
  onBlur?: (manufacturer: IManufacturer) => void;
  required?: boolean;
  disabled?: boolean;
  hasCreateOption?: boolean;
  isAlternateVersionEnabled?: boolean;
}

export const ManufacturerFormField = ({
  name = 'manufacturer',
  label,
  optionKey = 'name',
  requestPayload,
  onChange,
  onBlur,
  required = false,
  disabled = false,
  hasCreateOption: customHasCreateOption = undefined,
  isAlternateVersionEnabled = false,
}: IProps) => {
  const { t } = useTranslation();

  const { control, errors } = useFormContext();

  const hasCreateOption = useMemo(() => {
    if (customHasCreateOption !== undefined) {
      return customHasCreateOption;
    }

    return !isAlternateVersionEnabled;
  }, [customHasCreateOption, isAlternateVersionEnabled]);

  return (
    <FormField
      name={name}
      type={InputType.AUTOCOMPLETE}
      inputProps={{
        control: control,
        error: errors[name],
        label: label || t('labelManufacturer'),
        optionKey,
        placeholder: t('placeholderChooseManufacturer'),
        getOptions: manufacturerService.getMany,
        requestPayload: {
          ...requestPayload,
          alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
        },
        getLabel: (option: IManufacturer) => option.name,
        onChange: (_: SyntheticEvent, option: IManufacturer) => onChange?.(option),
        onBlur: (_: SyntheticEvent, option: IManufacturer) => onBlur?.(option),
        hasCreateOption,
        disabled,
        required,
      }}
    />
  );
};
